import React, { useEffect, useState } from 'react';
import SelectTwo from '../../../../components/SelectTwo';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { PackingOrderDataForm } from '../../../../app/models/PackingOrder';
import { getTodayLocalDateString } from '../../../../helpers';

type SelectOpt = { id: number | string; name: string };

interface Props {
  packingOrder: PackingOrderDataForm;
  setPackingOrder: (data: PackingOrderDataForm) => void;
  societies: SelectOpt[];
  centers?: SelectOpt[];
  warehouses?: SelectOpt[];
  onSocietyChange?: (societyId: number | undefined) => void;
  onCenterChange?: (centerId: number | undefined) => void;
  statusChoices: SelectOpt[];
  errorFields?: any;
  onSave: (data: PackingOrderDataForm) => void;
  fetching?: boolean;
}

const PackingOrderHeaderForm = ({
  packingOrder,
  setPackingOrder,
  societies,
  centers = [],
  warehouses = [],
  onSocietyChange,
  onCenterChange,
  errorFields,
  onSave,
  fetching = false
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
  useEffect(() => {
    setErrors(errorFields);
  }, [errorFields, setErrors]);

  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<{ value: string; label: string } | null>(null);
  const [startDateForInProgress, setStartDateForInProgress] = useState('');
  const [endDateForFinalize, setEndDateForFinalize] = useState('');

  const societyOptions = societies.map((s) => ({ value: s.id, label: String(s.name) }));
  const centerOptions = centers.map((c) => ({ value: c.id, label: String(c.name) }));
  const warehouseOptions = warehouses.map((w) => ({ value: w.id, label: String(w.name) }));

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setPackingOrder({ ...packingOrder, [name]: value });
  };

  const handleSocietyChange = (v: any) => {
    const societyId = v?.value ?? undefined;
    setPackingOrder({
      ...packingOrder,
      society_id: societyId as any,
      center_id: undefined,
      warehouse_id: undefined
    });
    onSocietyChange?.(societyId != null ? Number(societyId) : undefined);
  };

  const handleCenterChange = (v: any) => {
    const centerId = v?.value ?? undefined;
    setPackingOrder({
      ...packingOrder,
      center_id: centerId != null ? (centerId as number) : undefined,
      warehouse_id: undefined
    });
    onCenterChange?.(centerId != null ? Number(centerId) : undefined);
  };

  const handleWarehouseChange = (v: any) => {
    setPackingOrder({
      ...packingOrder,
      warehouse_id: (v?.value ?? undefined) as any
    });
  };

  // Determina el próximo estado y la etiqueta del botón
  const nextStatusMap: Record<string, { value: string; label: string; btnClass: string }> = {
    CREATED: { value: 'IN_PROGRESS', label: 'Iniciar Orden', btnClass: 'btn-warning' },
    IN_PROGRESS: { value: 'FINALIZED', label: 'Finalizar Orden', btnClass: 'btn-success' },
  };

  const statusLabelMap: Record<string, { label: string; badgeClass: string }> = {
    CREATED: { label: 'Creado', badgeClass: 'bg-secondary' },
    IN_PROGRESS: { label: 'En progreso', badgeClass: 'bg-warning text-dark' },
    FINALIZED: { label: 'Finalizado', badgeClass: 'bg-success' },
    CANCELLED: { label: 'Cancelado', badgeClass: 'bg-danger' },
  };

  const currentStatus = packingOrder.status ?? '';
  const nextStatus = nextStatusMap[currentStatus] ?? null;
  const currentStatusInfo = statusLabelMap[currentStatus] ?? { label: currentStatus, badgeClass: 'bg-secondary' };

  const handleStatusButtonClick = () => {
    if (!nextStatus) return;
    const today = getTodayLocalDateString();
    setPendingStatus({ value: nextStatus.value, label: nextStatus.label });
    if (nextStatus.value === 'IN_PROGRESS') {
      setStartDateForInProgress((packingOrder.start_date || '').slice(0, 10) || today);
    } else {
      setStartDateForInProgress('');
    }
    if (nextStatus.value === 'FINALIZED') {
      setEndDateForFinalize((packingOrder.end_date || '').slice(0, 10) || today);
    } else {
      setEndDateForFinalize('');
    }
    setShowStatusConfirmModal(true);
  };


  const confirmStatusChange = () => {
    if (!pendingStatus) return;
    const updated: PackingOrderDataForm = {
      ...packingOrder,
      status: pendingStatus.value as any
    };
    if (pendingStatus.value === 'IN_PROGRESS' && startDateForInProgress.trim()) {
      updated.start_date = startDateForInProgress.slice(0, 10);
    }
    if (pendingStatus.value === 'FINALIZED' && endDateForFinalize.trim()) {
      updated.end_date = endDateForFinalize.slice(0, 10);
    }
    // No actualizar el estado local aquí: el padre recarga desde el backend
    // en onSuccess y sólo entonces el badge cambia.
    onSave(updated);
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
    setStartDateForInProgress('');
    setEndDateForFinalize('');
  };

  const closeModal = () => {
    setShowStatusConfirmModal(false);
    setPendingStatus(null);
    setStartDateForInProgress('');
    setEndDateForFinalize('');
  };

  const confirmDisabled =
    (pendingStatus?.value === 'IN_PROGRESS' && !startDateForInProgress.trim()) ||
    (pendingStatus?.value === 'FINALIZED' && !endDateForFinalize.trim());

  return (
    <>
      {/* Fila 1: Sociedad, Fecha de Programa */}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label" htmlFor="society_id">
            Sociedad
          </label>
          <SelectTwo
            name="society_id"
            id="society_id"
            inputValue={packingOrder.society_id ?? null}
            options={societyOptions}
            hasError={fieldHasError('society_id') !== ''}
            onChange={handleSocietyChange}
            placeholder="Seleccione una sociedad"
            onFocus={() => onFocusRemove('society_id')}
          />
          <div className={`invalid-feedback ${fieldHasError('society_id') !== '' ? 'd-block' : ''}`} style={{ display: 'flex' }}>
            {fieldErrorMessage('society_id')}
          </div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label" htmlFor="program_date">
            Fecha de programa
          </label>
          <input
            type="date"
            name="program_date"
            id="program_date"
            className={`form-control ${fieldHasError('program_date')}`}
            value={packingOrder.program_date ?? ''}
            onChange={handleChange}
            onFocus={() => onFocusRemove('program_date')}
          />
          <div className={`invalid-feedback ${fieldHasError('program_date') !== '' ? 'd-block' : ''}`} style={{ display: 'flex' }}>
            {fieldErrorMessage('program_date')}
          </div>
        </div>
      </div>

      {/* Fila 2: Centro, Almacén */}
      <div className="mb-2 row">
        <div className="col-md-6">
          <label className="col-form-label" htmlFor="center_id">
            Centro
          </label>
          <SelectTwo
            name="center_id"
            id="center_id"
            inputValue={packingOrder.center_id ?? null}
            options={centerOptions}
            hasError={fieldHasError('center_id') !== ''}
            onChange={handleCenterChange}
            placeholder="Seleccione un centro"
            onFocus={() => onFocusRemove('center_id')}
          />
          <div className={`invalid-feedback ${fieldHasError('center_id') !== '' ? 'd-block' : ''}`} style={{ display: 'flex' }}>
            {fieldErrorMessage('center_id')}
          </div>
        </div>
        <div className="col-md-6">
          <label className="col-form-label" htmlFor="warehouse_id">
            Almacén
          </label>
          <SelectTwo
            name="warehouse_id"
            id="warehouse_id"
            inputValue={packingOrder.warehouse_id ?? null}
            options={warehouseOptions}
            hasError={fieldHasError('warehouse_id') !== ''}
            onChange={handleWarehouseChange}
            placeholder="Seleccione un almacén"
            onFocus={() => onFocusRemove('warehouse_id')}
          />
          <div className={`invalid-feedback ${fieldHasError('warehouse_id') !== '' ? 'd-block' : ''}`} style={{ display: 'flex' }}>
            {fieldErrorMessage('warehouse_id')}
          </div>
        </div>
      </div>

      {/* Fila 3: Fecha de Inicio, Estado, Fecha Fin */}
      <div className="mb-2 row">
        <div className="col-md-4">
          <label className="col-form-label">Fecha de inicio</label>
          <input
            name="start_date"
            type="date"
            className={`form-control ${fieldHasError('start_date')}`}
            value={packingOrder.start_date ?? ''}
            readOnly
            disabled
            title="Se registra al cambiar a En progreso"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('start_date')}</div>
        </div>
        <div className="col-md-4">
          <label className="col-form-label">Estado</label>
          <div className="d-flex align-items-center gap-2 mt-1">
            <span className={`badge fs-6 px-3 py-2 ${currentStatusInfo.badgeClass}`}>
              {currentStatusInfo.label}
            </span>
            {nextStatus && (
              <button
                type="button"
                className={`btn btn-sm ${nextStatus.btnClass}`}
                onClick={handleStatusButtonClick}
                disabled={!!fetching}
                title={`Cambiar estado a: ${nextStatus.label}`}
              >
                <i className="mdi mdi-arrow-right-circle me-1" />
                {nextStatus.label}
              </button>
            )}
          </div>
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('status')}</div>
        </div>
        <div className="col-md-4">
          <label className="col-form-label">Fecha fin</label>
          <input
            name="end_date"
            type="date"
            className={`form-control ${fieldHasError('end_date')}`}
            value={packingOrder.end_date ?? ''}
            readOnly
            disabled
            title="Se registra al cambiar a Finalizada"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('end_date')}</div>
        </div>
      </div>

      <div className="row justify-content-end mt-2">
        <div className="col-auto">
          <ButtonSaveForm callbackSave={() => onSave(packingOrder)} locked={!!fetching} />
        </div>
      </div>

      {/* Modal de confirmación de cambio de estado */}
      {showStatusConfirmModal && pendingStatus && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }}>
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirmar cambio de estado</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Cerrar"
                  />
                </div>
                <div className="modal-body">
                  <p>
                    ¿Desea confirmar el cambio de estado a <strong>{pendingStatus.label}</strong>?
                  </p>
                  {pendingStatus.value === 'IN_PROGRESS' && (
                    <div className="mb-3">
                      <label className="form-label">Fecha de inicio</label>
                      <input
                        type="date"
                        className="form-control"
                        value={startDateForInProgress}
                        onChange={(e) => setStartDateForInProgress(e.target.value)}
                      />
                      <small className="text-muted">Se registrará como fecha de inicio de la orden.</small>
                    </div>
                  )}
                  {pendingStatus.value === 'FINALIZED' && (
                    <div className="mb-3">
                      <label className="form-label">Fecha fin</label>
                      <input
                        type="date"
                        className="form-control"
                        value={endDateForFinalize}
                        onChange={(e) => setEndDateForFinalize(e.target.value)}
                      />
                      <small className="text-muted">Se registrará como fecha de cierre de la orden.</small>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    disabled={!!confirmDisabled}
                    onClick={confirmStatusChange}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PackingOrderHeaderForm;
